// The sidebar menu: one definition for the menu itself and for the rows of the user permissions screen, so the two can
// never differ. Each item says which of the older detailed permissions (pages and actions) it stands for, so every check
// written against those keeps working; a new item added here appears on the permissions screen as "No access" for
// everyone (except users who have everything set to Full).

export interface MenuItem {
  id: string; label: string; href: string; children?: MenuItem[];
  /** Shown in the menu with the access of another item (no row of its own): Ready for QC is part of QC. */
  sameAs?: string;
  /** The older permission pages and actions this item covers. */
  pages?: string[]; actions?: string[];
}

export const MENU: MenuItem[] = [
  { id: "dashboard", label: "Dashboard", href: "/app", pages: ["dashboard"] },
  { id: "tasks", label: "My Tasks", href: "/app/tasks", pages: ["tasks"], actions: ["updateTasks", "assignTasks"] },
  {
    id: "developmentgroup", label: "Development", href: "", children: [
      { id: "rd", label: "R&D", href: "/app/rd", pages: ["rd"], actions: ["manageRD", "convertRD"] },
      { id: "techpacks", label: "Tech Packs", href: "/app/techpacks", pages: ["techpacks"] },
      // CAD designs coloured per colourway and dressed with trims (Tech Pack users can read them to attach them).
      { id: "cad", label: "CAD Library", href: "/app/cad", pages: ["techpacks"] },
      { id: "artwork", label: "Artwork Library", href: "/app/artwork", pages: ["techpacks"], actions: ["approveArtwork"] },
    ],
  },
  {
    id: "productsgroup", label: "Products", href: "", children: [
      { id: "products", label: "Garments", href: "/app/garments", pages: ["products"] },
      { id: "fabric", label: "Fabrics", href: "/app/fabrics", pages: ["fabric"] },
      { id: "trims", label: "Trims & Accessories", href: "/app/trims", pages: ["products"] },
    ],
  },
  { id: "po", label: "Purchase Orders", href: "/app/po", pages: ["po"], actions: ["createPO", "editPO"] },
  { id: "critical", label: "Time & Action Plan", href: "/app/critical", pages: ["critical"], actions: ["editWorkflow", "updateBulk", "approveSamples", "approvePPS", "approveSTP"] },
  { id: "sampling", label: "Sampling", href: "/app/sampling", pages: ["sampling"], actions: ["manageSamples", "approveSamples", "approvePPS"] },
  // Sent date, courier and tracking for samples (several in one parcel), received and forwarded by the agent. Suppliers
  // see it as "Samples to send", agents as "Samples".
  { id: "dispatch", label: "Sample dispatch", href: "/app/samples", pages: ["dispatch"], actions: ["dispatchSamples"] },
  {
    id: "qualitygroup", label: "Quality", href: "", children: [
      { id: "qc", label: "QC", href: "/app/qc", pages: ["qc"], actions: ["performQC"] },
      { id: "qcready", label: "Ready for QC", href: "/app/qc/ready", sameAs: "qc" },
      { id: "compliance", label: "Compliance & Certification", href: "/app/compliance", pages: ["compliance"], actions: ["manageCompliance"] },
    ],
  },
  {
    id: "logisticsgroup", label: "Logistics", href: "", children: [
      { id: "shipmentcontrol", label: "Shipment Control", href: "/app/shipments", pages: ["shipmentcontrol"], actions: ["createShipment", "updateShipment", "markReceived", "managePackingList"] },
    ],
  },
  // Inventory (optional, per organisation; shown only when an Admin has switched it on in Settings → Inventory).
  {
    id: "inventorygroup", label: "Inventory", href: "", children: [
      { id: "invstock", label: "Stock on hand", href: "/app/inventory" },
      { id: "invreceive", label: "Goods receive", href: "/app/inventory/receipts" },
      { id: "invgrnenq", label: "Goods received enquiry", href: "/app/inventory/enquiry", sameAs: "invreceive" },
      { id: "invadjust", label: "Adjustments", href: "/app/inventory/adjustments" },
      { id: "invtransfer", label: "Transfers", href: "/app/inventory/transfers" },
      { id: "invmoves", label: "Movements", href: "/app/inventory/movements", sameAs: "invstock" },
    ],
  },
  {
    id: "financegroup", label: "Finance", href: "", children: [
      { id: "financeledgers", label: "Ledgers", href: "/app/finance/ledgers" },
      // Needs Full on Ledgers and "Can see costs and prices" (shown on the Ledgers row of the permissions screen).
      { id: "financeexport", label: "Export to accounting", href: "/app/finance/export", sameAs: "financeledgers" },
      // Claims and deductions: merchandisers raise them (no other Finance access or "Can see costs and prices" needed;
      // amounts show only with it); suppliers and agents see the claims sent to them and reply.
      { id: "financeclaims", label: "Claims", href: "/app/finance/claims" },
      { id: "financelc", label: "Letters of credit", href: "/app/finance/lc" },
      { id: "financelanded", label: "Landed cost", href: "/app/finance/landed", pages: ["landedcosting"], actions: ["manageLandedCost"] },
      { id: "financereports", label: "Reports", href: "/app/finance/reports" },
    ],
  },
  {
    id: "partnersgroup", label: "Partners", href: "", children: [
      { id: "supplierdb", label: "Suppliers", href: "/app/database/suppliers", pages: ["supplierdb"] },
      { id: "agentdb", label: "Agents", href: "/app/database/agents", pages: ["agentdb"] },
      { id: "logisticsdb", label: "Logistics providers", href: "/app/database/logistics" },
    ],
  },
  {
    id: "reportsgroup", label: "Reports", href: "", children: [
      { id: "timeline", label: "Order Timeline", href: "/app/reports/timeline", pages: ["po"] },
      { id: "supplierperformance", label: "Supplier Performance", href: "/app/performance", pages: ["supplierperformance"] },
      { id: "activity", label: "Activity Log", href: "/app/activity", pages: ["activity"], actions: ["viewActivity"] },
    ],
  },
];
/** The item at the foot of the sidebar (Help is always there for everyone and has no row). */
export const MENU_FOOTER: MenuItem[] = [
  { id: "settings", label: "Settings", href: "/app/settings", pages: ["settings", "operatordb", "database", "fabricdb"], actions: ["manageDatabase", "manageUsers", "managePermissions"] },
];
/** Rows on the permissions screen that are not menu items of their own, placed in a group after its items. */
export const EXTRA_ROWS: Array<{ id: string; label: string; group: string; hint: string }> = [
  { id: "qcstandard", label: "QC standard", group: "qualitygroup", hint: "Change the Inspection Standard / AQL" },
];

export interface AccessRow { id: string; label: string; group: string; groupLabel: string; extra?: boolean; hint?: string; also?: string[] }
/** One row per menu item (and the extra rows), in menu order; items with the access of another are noted on its row. */
export function accessRows(): AccessRow[] {
  const out: AccessRow[] = [];
  const add = (it: MenuItem, group = "", groupLabel = "") => {
    if (it.sameAs) { const r = out.find((x) => x.id === it.sameAs); if (r) r.also = [...(r.also || []), it.label]; return; }
    out.push({ id: it.id, label: it.label, group, groupLabel });
  };
  for (const it of [...MENU, ...MENU_FOOTER]) {
    if (it.children) {
      for (const c of it.children) add(c, it.id, it.label);
      for (const x of EXTRA_ROWS.filter((e) => e.group === it.id)) out.push({ id: x.id, label: x.label, group: it.id, groupLabel: it.label, extra: true, hint: x.hint });
    } else add(it);
  }
  return out;
}
export const ROW_IDS = (): string[] => accessRows().map((r) => r.id);
/** Every menu item (leaves), with its group. */
export function menuLeaves(): Array<MenuItem & { group: string }> {
  return [...MENU, ...MENU_FOOTER].flatMap((it) => (it.children ? it.children.map((c) => ({ ...c, group: it.id })) : [{ ...it, group: "" }]));
}
/** The row whose access an item uses. */
export const rowOf = (itemId: string): string => menuLeaves().find((m) => m.id === itemId)?.sameAs || itemId;
